import React, { forwardRef, useRef } from 'react';
import Webcam from 'react-webcam';
import {
  BIOMETRIC_VIDEO_CONSTRAINTS,
  BIOMETRIC_SCREENSHOT_FORMAT,
  BIOMETRIC_GUIDANCE,
} from '../constants/biometricCapture';

/**
 * Reusable biometric (face) capture component.
 * Uses shared constants for consistent video constraints and guidance across
 * registration, approval, and profile update flows.
 *
 * @param {Object} props
 * @param {'capture'|'continuous'} props.mode - 'capture' for manual capture with button; 'continuous' for auto-capture (parent uses ref)
 * @param {string} [props.statusText] - Status message (e.g. "Verifying..."); defaults to positioning in capture mode
 * @param {Function} [props.onCapture] - (imageSrc: string) => void - called when user captures (capture mode only)
 * @param {string} [props.captureButtonLabel] - Label for capture button (capture mode only)
 * @param {string} [props.containerClassName] - Class for webcam container (e.g. webcam-container-approval)
 * @param {boolean} [props.showTips] - Show secondary tips; default true
 */
const BiometricCapture = forwardRef(function BiometricCapture(
  {
    mode = 'capture',
    statusText,
    onCapture,
    captureButtonLabel = 'Capture Photo',
    containerClassName = '',
    showTips = true,
  },
  ref
) {
  const internalRef = useRef(null);
  const webcamRef = ref ?? internalRef;

  const handleCapture = () => {
    if (webcamRef?.current && onCapture) {
      const imageSrc = webcamRef.current.getScreenshot();
      if (imageSrc) onCapture(imageSrc);
    }
  };

  const displayStatus = statusText ?? BIOMETRIC_GUIDANCE.positioning;
  const containerClass = ['webcam-container', containerClassName]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <p className="biometric-capture-status">{displayStatus}</p>
      {showTips && (
        <p className="biometric-tips">{BIOMETRIC_GUIDANCE.tips}</p>
      )}
      <div className={containerClass}>
        <div className="webcam-overlay">
          <Webcam
            ref={webcamRef}
            audio={false}
            screenshotFormat={BIOMETRIC_SCREENSHOT_FORMAT}
            videoConstraints={BIOMETRIC_VIDEO_CONSTRAINTS}
            className="webcam-video"
          />
        </div>
        {mode === 'capture' && (
          <button
            type="button"
            onClick={handleCapture}
            className={containerClassName.includes('update') ? 'biometric-capture-btn' : ''}
          >
            {captureButtonLabel}
          </button>
        )}
      </div>
    </>
  );
});

export default BiometricCapture;
